import request from '@/utils/request';

/**
 * 获取所有区域
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *     alertConfig: string
 *   }>
 * }>} 区域列表
 */
export function getAllAreas() {
  return request({
    url: '/area',
    method: 'get'
  });
}

/**
 * 获取区域列表
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *     alertConfig: string
 *   }>
 * }>} 区域列表
 */
export function getAreaList(bridgeId) {
  return request({
    url: '/area/list',
    method: 'get',
    params: { bridgeId }
  });
}

/**
 * 根据桥梁ID获取区域列表
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *     alertConfig: string
 *   }>
 * }>} 区域列表
 */
export function getAreasByBridgeId(bridgeId) {
  console.log(`API调用: getAreasByBridgeId(${bridgeId})`);
  return request({
    url: `/area/bridge/${bridgeId}`,
    method: 'get'
  });
}

/**
 * 根据区域类型获取区域列表
 * @param {string} areaType 区域类型，可选值: BRIDGE, BRIDGE_LINE, BRIDGE_MAST, CHANNEL, CHANNEL_DOWN, CHANNEL_UP, NAVIGATION, NAVIGATION_DOWN, NAVIGATION_UP, NO_NAVIGATION, OTHER, RADAR_AREA_DOWN, RADAR_AREA_UP, REPORT_LINE_DOWN, REPORT_LINE_UP, RESTRICTED, WARNING, WARNING_LINE_DOWN, WARNING_LINE_UP
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *     alertConfig: string
 *   }>
 * }>} 区域列表
 */
export function getAreasByType(areaType) {
  return request({
    url: `/area/type/${areaType}`,
    method: 'get'
  });
}

/**
 * 根据区域编码获取区域信息
 * @param {string} areaCode 区域编码
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *     alertConfig: string
 *   }
 * }>} 区域信息
 */
export function getAreaByCode(areaCode) {
  return request({
    url: `/area/code/${areaCode}`,
    method: 'get'
  });
}

/**
 * 根据ID获取区域信息
 * @param {number} id 区域ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *     alertConfig: string
 *   }
 * }>} 区域信息
 */
export function getAreaById(id) {
  console.log(`API调用: getAreaById(${id})`);
  return request({
    url: `/area/${id}`,
    method: 'get'
  });
}

/**
 * 创建区域
 * @param {Object} data 区域数据
 * @param {string} data.areaName 区域名称
 * @param {string} data.areaCode 区域编码
 * @param {string} data.areaType 区域类型，可选值: BRIDGE, BRIDGE_LINE, BRIDGE_MAST, CHANNEL, CHANNEL_DOWN, CHANNEL_UP, NAVIGATION, NAVIGATION_DOWN, NAVIGATION_UP, NO_NAVIGATION, OTHER, RADAR_AREA_DOWN, RADAR_AREA_UP, REPORT_LINE_DOWN, REPORT_LINE_UP, RESTRICTED, WARNING, WARNING_LINE_DOWN, WARNING_LINE_UP
 * @param {string} data.coordinates 区域坐标(GeoJSON格式)
 * @param {number} [data.bridgeId] 关联桥梁ID
 * @param {string} [data.description] 区域描述
 * @param {number} [data.lineDirection] 线段方向
 * @param {number} [data.isEnabled] 是否启用
 * @param {number} [data.level] 区域级别
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *     alertConfig: string
 *   }
 * }>} 创建的区域信息
 */
export function createArea(data) {
  return request({
    url: '/area',
    method: 'post',
    data
  });
}

/**
 * 更新区域
 * @param {number} id 区域ID
 * @param {Object} data 区域数据
 * @param {string} data.areaName 区域名称
 * @param {string} data.areaCode 区域编码
 * @param {string} data.areaType 区域类型，可选值: BRIDGE, BRIDGE_LINE, BRIDGE_MAST, CHANNEL, CHANNEL_DOWN, CHANNEL_UP, NAVIGATION, NAVIGATION_DOWN, NAVIGATION_UP, NO_NAVIGATION, OTHER, RADAR_AREA_DOWN, RADAR_AREA_UP, REPORT_LINE_DOWN, REPORT_LINE_UP, RESTRICTED, WARNING, WARNING_LINE_DOWN, WARNING_LINE_UP
 * @param {string} data.coordinates 区域坐标(GeoJSON格式)
 * @param {number} [data.bridgeId] 关联桥梁ID
 * @param {string} [data.description] 区域描述
 * @param {number} [data.lineDirection] 线段方向
 * @param {number} [data.isEnabled] 是否启用
 * @param {number} [data.level] 区域级别
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     areaName: string,
 *     areaCode: string,
 *     areaType: string,
 *     coordinates: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     description: string,
 *     geometryType: string,
 *     lineDirection: number,
 *     isEnabled: number,
 *     level: number,
 *     createTime: string,
 *     updateTime: string,
 *     color: string,
 *     icon: string,
 *   }
 * }>} 更新后的区域信息
 */
export function updateArea(id, data) {
  return request({
    url: `/area/${id}`,
    method: 'put',
    data
  });
}

/**
 * 删除区域
 * @param {number} id 区域ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteArea(id) {
  return request({
    url: `/area/${id}`,
    method: 'delete'
  });
}

/**
 * 分页查询区域进出记录
 * @param {Object} params 查询参数
 * @param {number} [params.areaId] 区域ID
 * @param {string} [params.areaType] 区域类型
 * @param {number} [params.bridgeId] 桥梁ID
 * @param {string} [params.startTime] 开始时间
 * @param {string} [params.endTime] 结束时间
 * @param {number} [params.pageNum] 页码
 * @param {number} [params.pageSize] 每页记录数
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       areaId: number,
 *       areaName: string,
 *       areaType: string,
 *       bridgeId: number,
 *       vesselMmsi: string,
 *       vesselName: string,
 *       eventType: string,
 *       eventTime: string,
 *       longitude: number,
 *       latitude: number,
 *       createTime: string
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的区域进出记录
 */
export function getAreaRecordPage(params) {
  return request({
    url: '/area/record/page',
    method: 'get',
    params
  });
}

/**
 * 根据ID获取区域进出记录
 * @param {number} id 记录ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     areaId: number,
 *     areaName: string,
 *     areaType: string,
 *     bridgeId: number,
 *     vesselMmsi: string,
 *     vesselName: string,
 *     eventType: string,
 *     eventTime: string,
 *     longitude: number,
 *     latitude: number,
 *     createTime: string
 *   }
 * }>} 区域进出记录详情
 */
export function getAreaRecordById(id) {
  return request({
    url: `/area/record/${id}`,
    method: 'get'
  });
}

/**
 * 更新区域警戒配置
 * @param {number} areaId 区域ID
 * @param {Object} alertConfig 警戒配置
 * @param {string} [alertConfig.alertType] 警戒类型
 * @param {string} [alertConfig.alertLevel] 警戒级别，可选值参考ALERT_LEVEL字典
 * @param {boolean} [alertConfig.isEnabled] 是否启用
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     areaId: number,
 *     alertConfig: Object
 *   }
 * }>} 更新结果
 */
export function updateAreaAlertConfig(areaId, alertConfig) {
  return request({
    url: `/area/${areaId}/alert-config`,
    method: 'put',
    data: { alertConfig }
  });
}

/**
 * 获取区域警戒配置
 * @param {number} areaId 区域ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     areaId: number,
 *     alertType: string,
 *     alertLevel: string,
 *     isEnabled: boolean
 *   }
 * }>} 区域警戒配置
 */
export function getAreaAlertConfig(areaId) {
  return request({
    url: `/area/${areaId}/alert-config`,
    method: 'get'
  });
} 